import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO_IMG } from "@/data/products";
import { ArrowRight } from "lucide-react";

const lines = ["Reference-Grade", "Research", "Compounds."];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } } };
const line = { hidden: { y: "110%" }, show: { y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } } };

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[72vh] md:min-h-[80vh] flex flex-col justify-end overflow-hidden pt-28 pb-12 md:pb-16" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Laboratory vials" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/65 to-[#050505]/85" />
        <div className="absolute inset-0 bg-black/30 md:bg-black/10" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-5 md:mb-8">
          Molecularix Biolabs — Est. 2024
        </motion.p>
        <motion.h1 variants={container} initial="hidden" animate="show"
          className="font-serif font-light text-[2.75rem] sm:text-6xl md:text-8xl lg:text-9xl leading-[0.95] md:leading-[0.9] tracking-tight [text-shadow:0_2px_24px_rgba(0,0,0,0.55)]">
          {lines.map((l, idx) => (
            <span key={idx} className="hero-line-mask">
              <motion.span variants={line} className="block">{l}</motion.span>
            </span>
          ))}
        </motion.h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
          className="mt-8 flex flex-col md:flex-row md:items-end gap-6 md:justify-between">
          <p className="text-[#A1A1AA] max-w-md text-base md:text-lg leading-relaxed">
            A curated catalog of peptides and research chemicals, prepared to reference-grade specification for the modern laboratory.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/catalog" data-testid="hero-shop-btn"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">
              Enter Research Catalog <ArrowRight size={14} />
            </Link>
            <Link to="/library" data-testid="hero-explore-btn"
              className="inline-flex items-center gap-2 border border-white/25 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#F5F5F0] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              Explore Research
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
